import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { playerSectionService, SectionWithCategoriesDto, CategoryDto } from '@/features/game/services/sectionService';
import { useGameStore } from '@/features/game/store/gameStore';
import { useSubscriptionStore } from '@/features/game/store/subscriptionStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { PurchaseModal } from '@/components/shared/PurchaseModal';
import { Search, Info, ChevronDown, ChevronUp, X, Lock, Sparkles } from 'lucide-react';
import { API_BASE_URL } from '@/lib/api/apiClient';

const REQUIRED_CATEGORIES = 6;
const FREE_TRIAL_SECTION_NAME = 'القسم العام';
const API_URL = API_BASE_URL || '';
const ruqaaFont = { fontFamily: "'Aref Ruqaa', 'Amiri', serif" };
const arabicFont = { fontFamily: "'Almarai', 'Cairo', sans-serif" };

// Helper to get full image URL
const getImageUrl = (path?: string) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const base = API_URL || '';
  const p = path.startsWith('/') ? path : `/${path}`;
  return base ? `${base}${p}` : p;
};

// Map CategoryDto to the Category format expected by game store
const mapToCategory = (cat: CategoryDto) => ({
  id: cat.id,
  name: cat.name,
  description: cat.description,
  imageUrl: cat.imageUrl,
  questionCount: cat.questionCount,
});

export const GameSettingsPage = () => {
  const navigate = useNavigate();
  const { setGameSettings } = useGameStore();
  const { status: subscriptionStatus, fetchStatus, loading: subLoading } = useSubscriptionStore();
  
  const [sections, setSections] = useState<SectionWithCategoriesDto[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [showCategoryInfo, setShowCategoryInfo] = useState<string | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  useEffect(() => {
    loadData();
    fetchStatus();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const sectionsData = await playerSectionService.getSectionsWithCategories();
      setSections(sectionsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if this is a free trial (user hasn't used their free trial yet)
  const isFreeTrial = subscriptionStatus?.canPlayFree ?? true;
  
  // Check if user needs to purchase (used free trial and no games remaining)
  const needsPurchase = subscriptionStatus?.hasUsedFreeTrial && (subscriptionStatus?.gamesRemaining || 0) <= 0;

  // Filter sections: free trial يرى كل الأقسام، لكن فقط الفئات المفعّلة للمستخدمين الجدد قابلة للاختيار
  const getAvailableSections = () => sections;

  const availableSections = getAvailableSections();
  // Locked sections (paid, not in free trial) for display in "Locked Sections" block
  const lockedSections = availableSections.filter((s: SectionWithCategoriesDto) => s.name !== FREE_TRIAL_SECTION_NAME);
  // في التجربة المجانية: قابلة للاختيار فقط إذا مفعّلة للمستخدمين الجدد (أو من القسم العام عند عدم وجود الحقل من الـ API)
  const isCategorySelectableInFreeTrial = (cat: CategoryDto, sectionName?: string) =>
    cat.enableForNewUsers === true || (sectionName === FREE_TRIAL_SECTION_NAME && cat.enableForNewUsers !== false);

  const toggleCategory = (categoryId: string, category?: CategoryDto, sectionName?: string) => {
    if (isFreeTrial && category && !isCategorySelectableInFreeTrial(category, sectionName)) return;
    if (selectedCategories.includes(categoryId)) {
      setSelectedCategories(selectedCategories.filter(id => id !== categoryId));
    } else {
      if (selectedCategories.length < REQUIRED_CATEGORIES) {
        setSelectedCategories([...selectedCategories, categoryId]);
      }
    }
  };

  const removeCategory = (categoryId: string) => {
    setSelectedCategories(selectedCategories.filter(id => id !== categoryId));
  };

  const toggleSection = (sectionId: string) => {
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(sectionId)) {
      newCollapsed.delete(sectionId);
    } else {
      newCollapsed.add(sectionId);
    }
    setCollapsedSections(newCollapsed);
  };

  // Filter categories by search term
  const filterCategories = (categories: CategoryDto[]) => {
    if (!searchTerm.trim()) return categories;
    return categories.filter(cat => 
      cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cat.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Get all categories flat from available sections
  const allCategories = availableSections.flatMap(s => s.categories);

  // Get selected category objects
  const selectedCategoryObjects = allCategories.filter(c => selectedCategories.includes(c.id));

  const canProceed = selectedCategories.length === REQUIRED_CATEGORIES;

  const handleContinue = () => {
    if (!canProceed) return;

    // Check if needs purchase
    if (needsPurchase) {
      setShowPurchaseModal(true);
      return;
    }

    const selectedCategoryObjectsMapped = selectedCategoryObjects.map(mapToCategory);

    // Store in game store (lifelines will be selected in team setup)
    setGameSettings({
      categories: selectedCategoryObjectsMapped,
      lifelines: [], // Will be set per team in TeamSetupPage
      isFreeTrial: isFreeTrial,
    });

    navigate('/player/team-setup');
  };

  // Handle clicking on a locked section
  const handleLockedSectionClick = () => {
    setShowPurchaseModal(true);
  };

  if (loading || subLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex" style={arabicFont}>
      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        {/* Free Trial Banner */}
        {isFreeTrial && (
          <div className="mb-6 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-4 text-white shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg">تجربتك المجانية!</h3>
                <p className="text-green-100 text-sm">
                  اختر 6 فئات من الفئات المحددة «متاح للتجربة» — أثناء اللعب ستظهر فقط الأسئلة المفعّلة للتجربة. اشترِ للوصول لجميع الأقسام!
                </p>
              </div>
              <Button
                onClick={() => setShowPurchaseModal(true)}
                className="bg-white text-green-600 hover:bg-green-50 font-bold"
              >
                <Sparkles className="w-4 h-4 ml-2" />
                اشترِ الآن
              </Button>
            </div>
          </div>
        )}

        {/* Games Remaining Banner (for paid users) */}
        {!isFreeTrial && subscriptionStatus && (
          <div className="mb-6 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-4 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold">{subscriptionStatus.gamesRemaining}</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg">ألعابك المتبقية</h3>
                  <p className="text-blue-100 text-sm">
                    لديك {subscriptionStatus.gamesRemaining} ألعاب متبقية
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setShowPurchaseModal(true)}
                variant="outline"
                className="border-white text-white hover:bg-white/20"
              >
                اشترِ المزيد
              </Button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-2" style={ruqaaFont}>
            اختر الفئات
          </h1>
          <p className="text-gray-600 text-sm">
            {isFreeTrial 
              ? 'اختر 6 فئات من الفئات التي تحمل شارة «متاح للتجربة» فقط — الأسئلة المفعّلة للتجربة المجانية ستظهر أثناء اللعب'
              : '3 فئات للفريق، و 3 فئات للفريق المنافس بمجموع 6 فئات'
            }
          </p>
        </div>

        {/* Search Box */}
        <div className="max-w-md mx-auto mb-6">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="البحث باسم الفئة"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pr-10 py-3 rounded-full border-2 border-gray-300 focus:border-red-500"
            />
          </div>
        </div>

        {/* Available Sections with Categories */}
        <div className="space-y-6">
          {availableSections.map((section) => {
            const filteredCategories = filterCategories(section.categories);
            if (filteredCategories.length === 0) return null;
            
            const isCollapsed = collapsedSections.has(section.id);
            
            return (
              <div key={section.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className={`w-full px-6 py-4 flex items-center justify-between transition-all ${
                    isFreeTrial && section.name !== FREE_TRIAL_SECTION_NAME
                      ? 'bg-gray-400 text-gray-100'
                      : 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-bold">{section.name}</span>
                    {isFreeTrial && section.name === FREE_TRIAL_SECTION_NAME && (
                      <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                        مجاني
                      </span>
                    )}
                  </div>
                  {isCollapsed ? (
                    <ChevronDown className="w-6 h-6" />
                  ) : (
                    <ChevronUp className="w-6 h-6" />
                  )}
                </button>
                
                {/* Categories Grid */}
                {!isCollapsed && (
                  <div className="p-3">
                    <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2">
                      {filteredCategories.map((category) => {
                        const isSelected = selectedCategories.includes(category.id);
                        const canSelectInFreeTrial = !isFreeTrial || isCategorySelectableInFreeTrial(category, section.name);
                        const isDisabled = !canSelectInFreeTrial || (!isSelected && selectedCategories.length >= REQUIRED_CATEGORIES);
                        
                        return (
                          <div
                            key={category.id}
                            onClick={() => !isDisabled && toggleCategory(category.id, category, section.name)}
                            className={`relative group cursor-pointer transition-all duration-200 ${
                              isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            {/* Category Card - في التجربة المجانية: الفئة غير المفعّلة للمستخدمين الجدد تظهر باهتة */}
                            <div className={`relative rounded-xl overflow-hidden shadow-md transition-all ${
                              isSelected 
                                ? 'ring-4 ring-green-500 transform scale-105' 
                                : !canSelectInFreeTrial 
                                  ? 'grayscale opacity-70' 
                                  : 'hover:shadow-xl hover:scale-102'
                            }`}>
                              {/* Question Count Badge */}
                              {category.questionCount > 0 && (
                                <div className="absolute top-0 left-0 right-0 z-10">
                                  <div className="relative">
                                    <div className="absolute -top-0.5 left-1/2 transform -translate-x-1/2 bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-b-md font-bold whitespace-nowrap">
                                      {category.questionCount}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Info Icon */}
                              <button
                                className="absolute top-0.5 right-0.5 w-4 h-4 bg-blue-500 text-white rounded-full flex items-center justify-center z-20 opacity-70 hover:opacity-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowCategoryInfo(category.id);
                                }}
                              >
                                <Info className="w-2.5 h-2.5" />
                              </button>

                              {/* Category Image */}
                              <div className="aspect-square bg-gray-200">
                                {getImageUrl(category.imageUrl) ? (
                                  <img
                                    src={getImageUrl(category.imageUrl)!}
                                    alt={category.name}
                                    className="w-full h-full object-cover transition-all"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg';
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400">
                                    <span className="text-4xl">❓</span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Category Name */}
                              <div className="absolute bottom-0 left-0 right-0 bg-gray-700/90 p-1">
                                <h4 className="text-white text-[10px] font-bold text-center truncate">
                                  {category.name}
                                </h4>
                                {isFreeTrial && (
                                  <div className="text-center mt-0.5">
                                    {canSelectInFreeTrial ? (
                                      <span className="inline-block bg-green-500/90 text-white text-[9px] px-1.5 py-0.5 rounded font-bold">
                                        متاح للتجربة
                                      </span>
                                    ) : (
                                      <span className="inline-block bg-gray-500/90 text-white text-[9px] px-1.5 py-0.5 rounded">
                                        متاح بعد الشراء
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* Selection Overlay */}
                              {isSelected && (
                                <div className="absolute inset-0 bg-green-500/30 flex items-center justify-center">
                                  <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-xs shadow-lg">
                                    ✓
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Locked Sections (for free trial users) */}
          {lockedSections.map((section) => (
            <div 
              key={section.id} 
              className="bg-gray-100 rounded-2xl shadow-lg overflow-hidden cursor-pointer hover:bg-gray-200 transition-colors"
              onClick={handleLockedSectionClick}
            >
              <div className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Lock className="w-6 h-6 text-gray-400" />
                  <span className="text-xl font-bold text-gray-400">{section.name}</span>
                  <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-bold">
                    مدفوع
                  </span>
                </div>
                <Button
                  size="sm"
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white"
                >
                  فتح القسم
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Sidebar - Selected Categories */}
      <div className="w-80 bg-white shadow-xl p-4 flex flex-col">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-800 mb-2" style={ruqaaFont}>الفئات المختارة</h2>
          <p className="text-gray-500 text-sm">{selectedCategories.length} من {REQUIRED_CATEGORIES}</p>
          {isFreeTrial && selectedCategories.length > 0 && (
            <p className="text-green-600 text-xs font-medium mt-1 bg-green-50 rounded-lg px-2 py-1">
              الفئات المختارة متاحة في تجربتك المجانية — ستظهر فقط الأسئلة المفعّلة للتجربة أثناء اللعب
            </p>
          )}
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div 
              className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all"
              style={{ width: `${(selectedCategories.length / REQUIRED_CATEGORIES) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Selected Categories List */}
        <div className="flex-1 overflow-y-auto space-y-2">
          {selectedCategoryObjects.map((category, index) => (
            <div 
              key={category.id}
              className="flex items-center gap-3 bg-gray-50 rounded-xl p-2 relative"
            >
              <img
                src={getImageUrl(category.imageUrl) || (API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg')}
                alt={category.name}
                className="w-16 h-16 rounded-lg object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg';
                }}
              />
              <div className="flex-1">
                <p className="font-bold text-gray-800 text-sm">{category.name}</p>
                <p className="text-gray-500 text-xs">#{index + 1}</p>
              </div>
              <button
                onClick={() => removeCategory(category.id)}
                className="absolute -top-1 -left-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}

          {/* Empty slots */}
          {Array.from({ length: REQUIRED_CATEGORIES - selectedCategories.length }).map((_, index) => (
            <div 
              key={`empty-${index}`}
              className="flex items-center gap-3 bg-gray-100 rounded-xl p-2 border-2 border-dashed border-gray-300"
            >
              <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center">
                <span className="text-gray-400 text-2xl">?</span>
              </div>
              <div className="flex-1">
                <p className="text-gray-400 text-sm">اختر فئة</p>
              </div>
            </div>
          ))}
        </div>

        {/* Action Button */}
        <div className="mt-4 pt-4 border-t">
          <Button
            onClick={handleContinue}
            disabled={!canProceed}
            className={`w-full py-4 text-lg font-bold rounded-full transition-all ${
              canProceed
                ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-xl'
                : 'bg-gray-400 text-gray-200 cursor-not-allowed'
            }`}
          >
            {canProceed 
              ? (isFreeTrial ? 'ابدأ التجربة المجانية →' : 'المتابعة →')
              : `اختر ${REQUIRED_CATEGORIES - selectedCategories.length} فئات أخرى`
            }
          </Button>
          
          {isFreeTrial && canProceed && (
            <p className="text-center text-gray-500 text-xs mt-2">
              هذه تجربتك المجانية الوحيدة
            </p>
          )}
        </div>
      </div>

      {/* Category Info Modal */}
      {showCategoryInfo && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowCategoryInfo(null)}
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-md w-full m-4"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const category = allCategories.find(c => c.id === showCategoryInfo);
              if (!category) return null;
              return (
                <>
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src={getImageUrl(category.imageUrl) || (API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg')}
                      alt={category.name}
                      className="w-20 h-20 rounded-xl object-cover"
                    />
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{category.name}</h3>
                      <p className="text-gray-500 text-sm">{category.questionCount || 0} سؤال</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">
                    {category.description || 'لا يوجد وصف متاح لهذه الفئة'}
                  </p>
                  <Button
                    onClick={() => setShowCategoryInfo(null)}
                    className="w-full bg-gray-200 text-gray-800 hover:bg-gray-300"
                  >
                    إغلاق
                  </Button>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Purchase Modal */}
      <PurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        reason={needsPurchase ? 'no_games' : 'free_trial_used'}
        gamesRemaining={subscriptionStatus?.gamesRemaining || 0}
      />
    </div>
  );
};
